import {ReactElement, useContext, useEffect, useState} from "react";
import * as widgetCss from "./components/graph-table-widget/styles/css.module.scss";
import * as css from "./styles/css.module.scss";

import useAppStore from "../../hooks/useAppStore";

import CurrentFocusedYear from "./components/current-focused-year/CurrentFocusedYear";
import DashboardHeader from "./components/dashboard-header/DashboardHeader";

import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import {Skeleton} from "@zendeskgarden/react-loaders";
import {ThemeProvider} from "styled-components";
import {TopCardTagType, useGetWorkflowsForPropertyLazyQuery, VersionType} from "../../__generated__/generated_types";
import * as statCSS from "../../components/account-summary/styles/styles.module.scss";
import {IMenuTitleOption} from "../../components/menu-title/logic/menuTitle";
import MenuTitle from "../../components/menu-title/MenuTitle";
import {useUserWorkflow} from "../../components/user-workflows/logic/useUserWorkflow";
import {UserWorkflowSummaryCard} from "../../components/user-workflows/UserWorkflowSummaryCard";
import {AuthContext} from "../../contexts/AuthContext";
import {showWorkflows} from "../../global/feature_checkers";
import {ViziblyTheme} from "../../styles/zendesk-garden/ViziblyZDGTheme";
import {IFinancialEntityWithDepth} from "../../utils/account";
import {getMonths} from "../../utils/months-helper";
import useTopCardTagSettings from "../admin/top-card-tag-settings/data/useTopCardTagSettings";
import AccountOverviewTables from "./components/account-overview-tables/AccountOverviewTables";
import {GLAccountsGraph} from "./components/gl-accounts/GLAccountsGraph";
import GLAccountsTable from "./components/gl-accounts/GLAccountsTable";
import GraphTableWidget from "./components/graph-table-widget/GraphTableWidget";
import {OperationalGraph} from "./components/operational-graph/OperationalGraph";
import OperationalTable from "./components/operational-table/OperationalTable";
import OverviewCard, {TOverviewCardProps} from "./components/overview-card/OverviewCard";
import StartPlanningCards from "./components/sp-cards/StartPlanningCards";
import WidgetLoading from "./components/widget-loading/WidgetLoading";
import {TPlanDashChartData, TPlanDashChartSeriesData} from "./data/logic/commonWidgetData";
import {
    buildAccountMenuOptions,
    buildComparisonMenuForAcquisition,
    buildComparisonMenuOptions,
    filterParsedGlAccountDataForAcquisition,
    getGLAccountChartData,
    getVarianceColorForAccount,
    parseGLAccountData
} from "./data/logic/glAccountData";
import {
    formatValueForDataType,
    getHotTableDataFromParsedSeriesData,
    OpDriverMetricType,
    operationalMenuOptions
} from "./data/logic/operationalData";
import {parseDashboardData} from "./data/logic/planningDashboardData";
import useGLAccountData from "./data/useGLAccountData";
import useOperationalData, {TWidgetTableConfig} from "./data/useOperationalData";
import usePlanningDashboardData from "./data/usePlanningDashboardData";

export default function PlanningDashboard(): ReactElement {

    const appStore = useAppStore();
    const dashboardData = usePlanningDashboardData();
    const operationalData = useOperationalData();
    const glAccountData = useGLAccountData();
    const topCardSettings = useTopCardTagSettings();
    const { user } = useContext(AuthContext);
    const {
        propertyWorkflows,
        setPropertyWorkflows,
    } = useUserWorkflow();

    const [getPropertyWorkflows, { data: workflows }] = useGetWorkflowsForPropertyLazyQuery({
        fetchPolicy: "network-only"
    });

    const [overviewCards, setOverviewCards] = useState<TOverviewCardProps[]>([]);
    const [availableTopCardTags, setAvailableTopCardTags] = useState<TopCardTagType[] | undefined>();

    // Rents Widget Support
    // const [rentsData, setRentsData] = useState<IRentsDataParsed>();
    // const [rentsFirstYear, setRentsFirstYear] = useState<number>(0);

    useEffect(
        () => {
            if (dashboardData.loading
                || !dashboardData.data
                || !dashboardData.focusYear
                || availableTopCardTags == undefined
            ) {
                return;
            }

            let focusYear = dashboardData.focusYear;
            if(dashboardData.property && dashboardData.property.isAcquisitionProperty) {
                focusYear = {
                    year: dashboardData.property.reforecastYear,
                    type: VersionType.Reforecast
                };
            }

            const parsed = parseDashboardData(
                    dashboardData.data,
                    focusYear,
                    availableTopCardTags,
                    dashboardData.property?.isAcquisitionProperty ?? false
            );
            setOverviewCards(parsed.overviewCards);

            // to allow Workflows to demo, but not be available for normal users, for now
            if (dashboardData.property && (showWorkflows(user) || user?.isInternalUser)) {
                getPropertyWorkflows({
                    variables: {
                        propertyId: dashboardData.property.id,
                        budgetYear: dashboardData.budgetYear,
                    }
                });
            }
            // setRentsData(parsed.rents);
        },
        [dashboardData.property, dashboardData.loading, dashboardData.data, dashboardData.focusYear, dashboardData.budgetYear, availableTopCardTags, user]
    );

    useEffect(
        () => {
            appStore.set({isLoading: false});
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            return () => {
            };
        },
        []
    );

    useEffect(() => {
        topCardSettings.fetch([], [], dashboardData.budgetYear);
    }, [dashboardData.budgetYear]);

    useEffect(() => {
        if (topCardSettings.data && !topCardSettings.loading && topCardSettings.initialized) {
            setAvailableTopCardTags(topCardSettings.data.map(x => x.topCardTagType));
        }
    }, [topCardSettings.data, topCardSettings.loading, topCardSettings.initialized]);

    // GL Account Widget Support
    const [parsedGLData, setParsedGLData] = useState<TPlanDashChartSeriesData[]>([]);
    const [glAccountChartData, setGLAccountChartData] = useState<TPlanDashChartData>();
    const [glAccountInfo, setGLAccountInfo] = useState<IFinancialEntityWithDepth | undefined>();
    const [glAccountOptions, setGLAccountOptions] = useState<IMenuTitleOption[]>([]);
    const [glSelectedAccount, setGLSelectedAccount] = useState<number>(0);
    const [glComparisonOptions, setGLComparisonOptions] = useState<IMenuTitleOption[]>([]);
    const [glSelectedComparison, setGLSelectedComparison] = useState<number>(dashboardData.property?.isAcquisitionProperty ? 1 : 0);
    const [glAccountsTableConfig, setGLAccountsTableConfig] = useState<TWidgetTableConfig | undefined>();
    const [glChangeVarianceColor, setGLChangeVarianceColor] = useState<string>('');

    useEffect(
        () => {
            if (!glAccountData.data || glAccountData.data.length == 0 || glAccountData.startMonthIndex == undefined || !glAccountData.coaWithDepths) {
                return;
            }

            let parsedData = parseGLAccountData(
                glAccountData.data,
                glAccountData.startMonthIndex,
            );

            let _comparisonOptions: IMenuTitleOption[];
            const focusYear = glAccountData.focusYear;
            if(dashboardData.property && dashboardData.property.isAcquisitionProperty) {
                parsedData = filterParsedGlAccountDataForAcquisition(parsedData);
                _comparisonOptions = buildComparisonMenuForAcquisition(parsedData);

                setParsedGLData(parsedData);
                setGLComparisonOptions(_comparisonOptions);
            } else {
                setParsedGLData(parsedData);

               _comparisonOptions = buildComparisonMenuOptions(glAccountData.data);
                setGLComparisonOptions(_comparisonOptions);
            }

            const builtChartData = getGLAccountChartData(parsedData, _comparisonOptions, glSelectedComparison, focusYear);
            setGLAccountChartData(builtChartData);

            const tableData = getHotTableDataFromParsedSeriesData({
                varianceCounts: builtChartData.varianceCounts,
                chartSeriesData: builtChartData.chartSeriesData,
            }, builtChartData.summaryData, 'dollar', getMonths(dashboardData.property));
            setGLAccountsTableConfig(tableData);

            const varianceColor = getVarianceColorForAccount(glAccountData.coaWithDepths[glSelectedComparison], builtChartData.summaryData?.varianceAmount?.value);
            setGLChangeVarianceColor(varianceColor);

            setGLAccountInfo(glAccountData.coaWithDepths[glSelectedComparison]);
        },
        [glAccountData.isReady, dashboardData.property]
    );

    useEffect(
        () => {
            if (glSelectedAccount == undefined || !glAccountData.coaWithDepths) {
                return;
            }

            const account = glAccountData.coaWithDepths[glSelectedAccount];

            if (account) {
                glAccountData.fetch(account.id);
            }
        },
        [glSelectedAccount]
    );

    useEffect(
        () => {
            if (glSelectedAccount == undefined || glSelectedComparison == undefined || !glAccountData.coaWithDepths || !glAccountData.data || glAccountData.data.length == 0) {
                return;
            }

            const builtChartData = getGLAccountChartData(parsedGLData, glComparisonOptions, glSelectedComparison, glAccountData.focusYear);
            setGLAccountChartData(builtChartData);

            const tableData = getHotTableDataFromParsedSeriesData({
                varianceCounts: builtChartData.varianceCounts,
                chartSeriesData: builtChartData.chartSeriesData,
            }, builtChartData.summaryData, 'dollar');

            setGLAccountsTableConfig(tableData);

            const varianceColor = getVarianceColorForAccount(glAccountData.coaWithDepths[glSelectedComparison], builtChartData.summaryData?.varianceAmount?.value);
            setGLChangeVarianceColor(varianceColor);
        },
        [glSelectedComparison]
    );

    useEffect(
        () => {
            if (!glAccountData.coaWithDepths) {
                return;
            }

            const _accountOptions = buildAccountMenuOptions(glAccountData.coaWithDepths);
            setGLAccountOptions(_accountOptions);
        },
        [glAccountData.coaWithDepths]
    );

    useEffect(() => {
        if (workflows?.getWorkflowsForProperty) {
            setPropertyWorkflows(workflows.getWorkflowsForProperty);
        }
    }, [workflows]);

    return (
        <div className={css.perfDashContainer}>
            <ThemeProvider theme={ViziblyTheme}>
                <DashboardHeader>
                    <DashboardHeader.Timeframe>
                        <span>Dashboard</span><span>//</span><CurrentFocusedYear
                            year={
                                dashboardData.focusYear?.year
                            }
                            yearType={dashboardData.focusYear?.type} />
                    </DashboardHeader.Timeframe>
                </DashboardHeader>

                {
                    propertyWorkflows.length > 0 &&
                    <div>
                        <h4 style={{marginTop: "1rem"}}>Workflows</h4>
                        <Row className={css.dashboardRow}>
                            {
                                propertyWorkflows.filter(workflow => workflow.tasks.length > 0).map((workflow, i) =>
                                    <Col key={i} md={3}>
                                        <UserWorkflowSummaryCard workflow={workflow} />
                                    </Col>
                                )
                            }
                        </Row>
                    </div>
                }

                <StartPlanningCards />

                <Row className={css.dashboardRow}>
                    {
                        overviewCards.length > 0 ?
                        overviewCards.map((cardInfo, idx) =>
                                <Col key={`overviewCard_${idx}`}>
                                    <OverviewCard
                                            {...cardInfo}
                                    />
                                </Col>
                        )
                        : [1, 2, 3, 4, 5].map(x =>
                            <Col key={x}>
                                <Skeleton height={'118px'} />
                            </Col>
                        )
                    }
                </Row>

                <Row className={css.dashboardRow}>
                    <Col md={12} lg={6}>
                        <GraphTableWidget>
                            <GraphTableWidget.Label>
                                <div className={widgetCss.widgetLabel}>
                                    <MenuTitle
                                        options={glAccountOptions}
                                        onChange={(newVal) => {
                                            setGLSelectedAccount(newVal);
                                        }}
                                        autocomplete
                                        noUnderline
                                    />
                                </div>

                                <div className={widgetCss.widgetSublabel}>
                                        {
                                            dashboardData.focusYear ?
                                                <MenuTitle
                                                    options={glComparisonOptions}
                                                    onChange={(newVal) => {
                                                        setGLSelectedComparison(newVal);
                                                    }}
                                                    small
                                                    plain
                                                />
                                                : <WidgetLoading />
                                        }
                                        <span className={widgetCss.widgetSublabelMetricDivider}>vs</span>
                                        <MenuTitle
                                            options={[
                                                {
                                                    label: glAccountChartData?.summaryData?.comparisonItemTwo.label ?? `${(glAccountData.focusYear + 1) % 100} BDGT`,
                                                    value: glAccountData.focusYear,
                                                }
                                            ]}
                                            small
                                            plain
                                        />
                                    </div>
                            </GraphTableWidget.Label>

                                <GraphTableWidget.Graph>
                                    {
                                        glAccountData.focusYear && glAccountChartData?.summaryData ?
                                        <Grid>
                                            <Row justifyContent="center" className={css.dashboardRow}>
                                                <Col textAlign="center">
                                                    <h5 className={statCSS.statLabel}>{glAccountChartData.summaryData.comparisonItemOne.label}</h5>
                                                    <h5 className={statCSS.statValue}>{glAccountChartData.summaryData.comparisonItemOne.value}</h5>
                                                </Col>

                                                <Col textAlign="center">
                                                    <h5 className={statCSS.statLabel}>{glAccountChartData.summaryData.comparisonItemTwo.label}</h5>
                                                    <h5 className={statCSS.statValue}>{glAccountChartData.summaryData.comparisonItemTwo.value}</h5>
                                                </Col>

                                                <Col textAlign="center">
                                                    <h5 className={statCSS.statLabel}>% Change</h5>
                                                    <h5
                                                        className={statCSS.statValue}
                                                        style={{'color': glChangeVarianceColor}}
                                                    >
                                                        {glAccountChartData.summaryData.variancePercent.value}
                                                    </h5>
                                                </Col>

                                                <Col textAlign="center">
                                                    <h5 className={statCSS.statLabel}>$ Change</h5>
                                                    <h5
                                                        className={statCSS.statValue}
                                                        style={{'color': glChangeVarianceColor}}
                                                    >
                                                        {formatValueForDataType(glAccountChartData.summaryData.varianceAmount.value, 'dollar')}
                                                    </h5>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                {
                                                    parsedGLData ?
                                                        <GLAccountsGraph chartData={glAccountChartData} months={getMonths(dashboardData.property)} />
                                                        : <WidgetLoading />
                                                }
                                                </Col>
                                            </Row>
                                        </Grid>
                                        : <WidgetLoading />
                                    }
                                </GraphTableWidget.Graph>

                            <GraphTableWidget.Table>
                                {
                                    glAccountsTableConfig ?
                                        <GLAccountsTable tableData={glAccountsTableConfig} account={glAccountInfo} />
                                        : <WidgetLoading />
                                }
                            </GraphTableWidget.Table>
                        </GraphTableWidget>
                    </Col>

                    <Col md={12} lg={6}>
                        {
                            dashboardData.focusYear != undefined ?
                            <GraphTableWidget>
                                <GraphTableWidget.Label>
                                    <div className={widgetCss.widgetLabel}>
                                        <MenuTitle
                                            options={operationalMenuOptions}
                                            onChange={(newVal) => {
                                                operationalData.setArgs({
                                                    metricType: newVal as OpDriverMetricType,
                                                });
                                            }}
                                            noUnderline
                                        />
                                    </div>
                                    <div className={widgetCss.widgetSublabel}>
                                        {!dashboardData.property?.isAcquisitionProperty &&
                                            <>
                                                <MenuTitle
                                                    options={operationalData.comparisonOptions}
                                                    onChange={(newVal) => {
                                                        operationalData.setArgs({
                                                            comparisonLeft: newVal,
                                                        });
                                                    }}
                                                    small
                                                    plain
                                                />
                                                <span className={widgetCss.widgetSublabelMetricDivider}>vs</span>
                                            </>
                                        }
                                        <MenuTitle
                                            options={[
                                                {
                                                    label: operationalData.summaryData?.comparisonItemTwo.label ?? `${dashboardData.focusYear.year % 100} BDGT`,
                                                    value: 0,
                                                }
                                            ]}
                                            small
                                            plain
                                        />
                                    </div>
                                </GraphTableWidget.Label>
                                <GraphTableWidget.Graph>
                                    <Grid>
                                        <Row justifyContent="center" className={css.dashboardRow}>
                                            {!dashboardData.property?.isAcquisitionProperty &&
                                                <Col textAlign="center">
                                                    <h5 className={statCSS.statLabel}>{operationalData.summaryData?.comparisonItemOne.label}</h5>
                                                    <h5 className={statCSS.statValue}>{operationalData.summaryData?.comparisonItemOne.value}</h5>
                                                </Col>
                                            }

                                            <Col textAlign="center">
                                                <h5 className={statCSS.statLabel}>{operationalData.summaryData?.comparisonItemTwo.label}</h5>
                                                <h5 className={statCSS.statValue}>{operationalData.summaryData?.comparisonItemTwo.value}</h5>
                                            </Col>

                                            {!dashboardData.property?.isAcquisitionProperty &&
                                                <>
                                                    <Col textAlign="center">
                                                        <h5 className={statCSS.statLabel}>{operationalData.summaryData?.variancePercent.label}</h5>
                                                        <h5 className={statCSS.statValue}>{operationalData.summaryData?.variancePercent.value}</h5>
                                                    </Col>

                                                    <Col textAlign="center">
                                                        <h5 className={statCSS.statLabel}>{operationalData.summaryData?.varianceAmount.label}</h5>
                                                        <h5 className={statCSS.statValue}>{operationalData.summaryData?.varianceAmount.value}</h5>
                                                    </Col>
                                                </>
                                            }
                                        </Row>

                                        <Row>
                                            <Col>
                                                {
                                                    operationalData.chartData ?
                                                    <OperationalGraph
                                                        chartData={operationalData.chartData}
                                                        dataType={operationalData.dataType}
                                                        months={getMonths(dashboardData.property)}
                                                    />
                                                    : <WidgetLoading />
                                                }
                                            </Col>
                                        </Row>
                                    </Grid>
                                </GraphTableWidget.Graph>
                                <GraphTableWidget.Table>
                                    {
                                        operationalData.tableData !== undefined ?
                                            <OperationalTable tableData={operationalData.tableData} />
                                            : <WidgetLoading />
                                    }
                                </GraphTableWidget.Table>
                            </GraphTableWidget>
                            : <WidgetLoading />
                        }
                    </Col>

                    {/* TODO after rents widget enabled, move operational widget to new row below this one and uncomment rents code */}
                    {/* <Col size="5">
                        <GraphTableWidget>
                            <GraphTableWidget.Label>
                                <div className={widgetCss.widgetLabel}>
                                    Rents
                                </div>
                                <div className={widgetCss.widgetSublabel}>
                                    {
                                        rentsData && dashboardData.focusYear?.year
                                            ? <RentsRange
                                                data={rentsData}
                                                prefs={dashboardData.rentsPrefs}
                                                year={dashboardData.focusYear?.year}
                                                onChange={(value: number) => {
                                                    setRentsFirstYear(value);
                                                }}
                                            />
                                            : <WidgetLoading />
                                    }
                                </div>
                            </GraphTableWidget.Label>
                            <GraphTableWidget.Graph>
                                {
                                    rentsData !== undefined
                                        ? <RentsGraph
                                            data={rentsData}
                                            firstYear={rentsFirstYear}
                                        />
                                        : <WidgetLoading />
                                }
                            </GraphTableWidget.Graph>
                            <GraphTableWidget.Table>
                                {
                                    rentsData !== undefined
                                        ? <RentsTable
                                            data={rentsData}
                                            firstYear={rentsFirstYear}
                                        />
                                        : <WidgetLoading />
                                }
                            </GraphTableWidget.Table>
                        </GraphTableWidget>
                    </Col> */}
                </Row>

                <Row className={css.dashboardRow}>
                    <Col>
                        <AccountOverviewTables className={css.widgetLabel} budgetYear={dashboardData.budgetYear}/>
                    </Col>
                </Row>
            </ThemeProvider>
        </div>
    );
}